// ---------------------------------------------------------------------------
// Connection status.
//
// The copy is about the state of the player's ANSWER, not about elapsed time or
// signal strength, because that is the only thing a player on a bad train line
// actually wants to know. Somebody watching this indicator is asking "if I drop
// now, do I lose the word I just typed?"
//
//   (nothing typed)      connected            there is no answer to lose yet
//   answer synced        the server has our draft
//   saving your answer   a draft is out and has not come back
//   answer not saved yet the socket is down with a draft still in flight
//   reconnecting         the socket is down but our answer did land
//
// Two things this must never do. It must not claim an answer is being saved
// when nothing is in flight, and it must not nag in the lobby, where there is
// no answer at all — "answer synced" under a lobby with no round running reads
// as a non sequitur.
//
// Staleness is measured against `serverNow()`, never `Date.now()`: the draft
// acknowledgement carries a server timestamp and a device clock minutes out
// would read every fresh draft as stale.
// ---------------------------------------------------------------------------

import { useEffect, useState, type ReactElement } from 'react';
import type { ConnectionQuality } from '@countdown/shared';
import { serverNow, useGameStore } from '../../store/gameStore.js';
import { DISCONNECT_GRACE_MS, useHeldTrue } from '../../hooks/useHeldTrue.js';
import { SignalBars } from './glyphs.js';

export interface ConnectionStatusProps {
  quality: ConnectionQuality;
  /** Compact form sits next to the round chip during play. */
  compact?: boolean;
  /**
   * Override the outstanding draft, which otherwise comes from the store.
   *
   * There is exactly one draft in flight per client, so the store is the right
   * home for it and the app never passes this. It exists so the component can be
   * driven entirely by props in a test or in the gallery at `/dev` — without it,
   * two of the five states could not be reached at all except by reaching into
   * global state, and two instances on one page could never disagree.
   */
  pendingSince?: number | null;
}

export type SyncState = 'idle' | 'synced' | 'saving' | 'unsaved' | 'offline';

/**
 * How long a draft may be in flight before we say anything about it.
 *
 * A healthy round trip is tens of milliseconds, so without this the indicator
 * would flicker through "saving your answer" on every keystroke — motion in the
 * corner of the eye during the one part of the game that needs concentration.
 * Past this, the save genuinely is slow and worth reporting.
 */
const SAVE_GRACE_MS = 1200;

export const SYNC_COPY: Record<SyncState, string> = {
  idle: 'connected',
  synced: 'answer synced',
  saving: 'saving your answer',
  unsaved: 'answer not saved yet',
  offline: 'reconnecting',
};

export function syncStateOf(
  quality: ConnectionQuality,
  pendingSince: number | null = null,
  now: number = serverNow(),
): SyncState {
  if (quality.status === 'offline' || quality.status === 'reconnecting') {
    // Nothing in flight means nothing was lost, however bad the connection is.
    return pendingSince === null ? 'offline' : 'unsaved';
  }
  if (pendingSince !== null) {
    return now - pendingSince > SAVE_GRACE_MS ? 'saving' : 'synced';
  }
  return quality.draftSyncedAt === null ? 'idle' : 'synced';
}

const BAR_LEVEL: Record<SyncState, 1 | 2 | 3> = {
  idle: 3,
  synced: 3,
  saving: 2,
  unsaved: 1,
  offline: 1,
};

/**
 * Re-render once the grace period is up, so a draft that never comes back is
 * eventually reported. Without this the component would sit on whatever it
 * decided at the keystroke that queued the draft.
 */
function useSyncState(quality: ConnectionQuality, override?: number | null): SyncState {
  const fromStore = useGameStore((store) => store.draftPendingSince);
  const pendingSince = override === undefined ? fromStore : override;
  const [, bump] = useState(0);

  useEffect(() => {
    if (pendingSince === null) return;
    const due = pendingSince + SAVE_GRACE_MS - serverNow();
    if (due <= 0) return;
    const timer = setTimeout(() => bump((tick) => tick + 1), due + 50);
    return () => clearTimeout(timer);
  }, [pendingSince]);

  return syncStateOf(quality, pendingSince);
}

export function ConnectionStatus({
  quality,
  compact = false,
  pendingSince,
}: ConnectionStatusProps): ReactElement {
  const raw = useSyncState(quality, pendingSince);

  // A socket that comes straight back was never worth reporting. Backgrounding
  // a browser on a phone and returning to it drops and restores the connection
  // inside a few hundred milliseconds, and the bars dipping and the copy
  // changing on the way through is motion that describes nothing.
  //
  // Held for `offline` ONLY. `unsaved` is the same dropped socket with a draft
  // still in flight, and that answer is at risk from the first millisecond — a
  // grace period there would be hiding the one state this indicator exists for.
  const settled = useHeldTrue(raw === 'offline', DISCONNECT_GRACE_MS);
  const state: SyncState =
    raw === 'offline' && !settled
      ? // What it would be reading if the socket were fine, which for the whole
        // of that second is the likelier truth.
        quality.draftSyncedAt === null
        ? 'idle'
        : 'synced'
      : raw;

  const copy = SYNC_COPY[state];

  // Compact form is glyph-only, at one fixed size in every state. It sits inline
  // beside the round chip and the target number, and copy that appears and
  // disappears there shoves the rest of the row sideways mid-round. A player who
  // has genuinely lost the connection gets the disconnect sheet, which is a
  // better place to say so than four words in a corner.
  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 rounded-chip px-2 py-1.5 transition-colors duration-300 ${
          state === 'unsaved' ? 'bg-alert/15 text-alert' : 'text-ink-dim'
        }`}
        data-sync-state={state}
        title={copy}
      >
        <SignalBars level={BAR_LEVEL[state]} />
        <span className="sr-only" role="status">
          {copy}
        </span>
      </div>
    );
  }

  // The full form is a label and a mark, not a tile. It was a bordered box with
  // a fixed 15ch label, which is what a tile in a grid of tiles wants; its one
  // caller is the last thing in a right-aligned header row, where a box drew a
  // second frame around something that is already inside one and the fixed width
  // held the text away from the edge it is aligned to.
  //
  // The label comes BEFORE the mark, so the signal glyph is the rightmost thing
  // in the row and lines up with the edge. `text-right` is what replaces the
  // fixed width: 15ch existed so the five sync strings could not resize the row
  // between them, and right-aligned copy at the end of a right-aligned row grows
  // leftward into space that is empty either way, so nothing to its left moves.
  // Putting this anywhere that is not right-aligned brings the shuffle back.
  return (
    <div
      className={`flex items-center gap-2 transition-colors duration-300 ${
        state === 'unsaved' ? 'text-alert' : 'text-ink-muted'
      }`}
      data-sync-state={state}
    >
      <span className="text-right font-mono text-[12px] whitespace-nowrap" role="status">
        {copy}
      </span>
      <SignalBars level={BAR_LEVEL[state]} />
    </div>
  );
}
